import dayjs from "dayjs";
import { type ICol, type ISection } from "src/interfaces";
import { type IReportEntry } from "src/pages/Reports/Report/Report.types";
import { type IChartCell } from "src/redux/slices/entriesGeneratorSlice";

import { isChartEntry } from "../CreateChart/buildChartData";
import {
  extractCellValue,
  findLeafColumnIndex,
  getCellAtLeafIndex,
  getEntryLocalDate,
  getLeafColumns,
  getRowCols,
  getWorkingHeaders,
} from "../CreateChart/CreateChartButton.utils";
import { isNumericValue } from "./Chart.utils";

export interface ICellRef {
  lbeid: string;
  sectionIndex: number;
  rowIndex: number;
  subRowIndex?: number;
  leafIndex: number;
}

export interface ICellPoint {
  time: number;
  value: number;
  lbeid: string;
  rowIndex: number;
  subRowIndex?: number;
  // Set where a row gives several values, one per reading.
  leafIndex?: number;
  // The reading's own time of day, where the table has a row of times.
  slot?: string;
}

export interface ICellComparison {
  label: string;
  point?: ICellPoint;
  delta?: number;
  percent?: number;
}

export interface ICellStats {
  // The latest entry with the lowest and with the highest value.
  min: ICellPoint;
  max: ICellPoint;
  average: number;
  count: number;
}

export interface ICellTrend {
  current: ICellPoint;
  // Oldest first, ending at the current point.
  history: ICellPoint[];
  comparisons: ICellComparison[];
  // Over every value in the report, later ones included.
  stats: ICellStats;
}

const WINDOWS = [
  { label: "Last week", days: 7 },
  { label: "Last month", days: 30 },
  { label: "Last Qtr", days: 90 },
];

const round2 = (value: number) => Math.round(value * 100) / 100;

// Numbers that identify something instead of measuring it.
const NOT_MEASURED = new Set([
  "check",
  "date",
  "time",
  "24lock",
  "imo",
  "mmsi",
  "ofnum",
  "dnl",
  "sign",
  "gross",
  "net",
  "len",
  "yachtname",
  "port",
  "user",
  "userref",
  "users",
  "userrank",
  "link",
  "attach",
  "large/text",
  "sec",
  "loclookup",
  "locport",
  "lat",
  "lat2",
  "lat3",
  "lat4",
  "long",
  "long2",
  "long3",
  "long4",
  "steering.pumps",
]);

// The header knows the column's kind even where an old entry's cell does not.
export const isMeasuredUnit = (unit: unknown) =>
  !NOT_MEASURED.has(String(unit));

// Entries saved without a date come back as none or as 1970.
const EARLIEST_TIME = dayjs("2000-01-01").valueOf();

// A checkbox's true would otherwise count as 1.
export const isTrendValue = (col?: ICol) => {
  const value = extractCellValue(col);
  return (
    isMeasuredUnit(col?.unit) &&
    typeof value !== "boolean" &&
    isNumericValue(value)
  );
};

// Added rows have no fixed position, so their column is one series.
export const isDynamicSection = (section?: ISection) =>
  Boolean(section?.drows) ||
  Boolean(section?.dynamicRows) ||
  Boolean(section?.cloneRows);

const fixedRows = new WeakMap<IReportEntry[], Map<string, boolean>>();

// Not every table that grows is flagged: the entries have the last word.
export const hasFixedRows = (
  entries: IReportEntry[],
  lbid: string,
  sectionIndex: number,
) => {
  const cache = fixedRows.get(entries) ?? new Map<string, boolean>();
  fixedRows.set(entries, cache);
  const key = `${lbid}|${sectionIndex}`;
  let fixed = cache.get(key);
  if (fixed === undefined) {
    const sections = entries
      .filter(entry => entry.lbid === lbid && isChartEntry(entry, true))
      .map(entry => entry.lbentry?.sections?.[sectionIndex]);
    const counts = new Set(
      sections.map(section => getSectionRows(section?.rows).length),
    );
    fixed = counts.size <= 1 && !sections.some(isDynamicSection);
    cache.set(key, fixed);
  }
  return fixed;
};

export const toTrendCell = (
  sectionIndex: number,
  rowIndex: number,
  subRowIndex?: number,
) => `${sectionIndex}:${rowIndex}:${subRowIndex ?? ""}`;

export const parseTrendCell = (
  lbeid: string,
  cell: string,
): ICellRef | null => {
  const [sectionIndex, rowIndex, subRowIndex, leafIndex] = cell.split(":");
  const ref = {
    lbeid,
    sectionIndex: Number(sectionIndex),
    rowIndex: Number(rowIndex),
    subRowIndex: subRowIndex === "" ? undefined : Number(subRowIndex),
    leafIndex: Number(leafIndex),
  };
  const valid = [ref.sectionIndex, ref.rowIndex, ref.leafIndex].every(
    index => Number.isInteger(index) && index >= 0,
  );
  return lbeid && leafIndex !== undefined && valid ? ref : null;
};

// Mirrors Section.tsx: rows can hold groups of sub-rows.
export const getSectionRows = (rows: unknown) => {
  if (!Array.isArray(rows)) return [];
  const isSubRows = rows[0]?.[0] && Array.isArray(rows[0][0]);
  return rows.flatMap((row, rowIndex) =>
    isSubRows && Array.isArray(row)
      ? row.map((subRow, subRowIndex) => ({
          cols: getRowCols(subRow),
          rowIndex,
          subRowIndex: subRowIndex as number | undefined,
        }))
      : [{ cols: getRowCols(row), rowIndex, subRowIndex: undefined }],
  );
};

// Spacer rows are named with a zero-width space, at times spelled out.
const INVISIBLE = /[\u200B-\u200D\uFEFF]|\\u200[B-D]/gi;

const cleanName = (name: unknown) =>
  String(name ?? "")
    .replaceAll("<br>", " ")
    .replace(INVISIBLE, "")
    .replace(/\s+/g, " ")
    .trim();

const isLabelCell = (col: ICol) =>
  Boolean(col.headerStyle) || col.unit === "BLANK";

// A row is named by a header-styled cell, or by a fixed text in front of it.
export const getRowLabel = (cols: ICol[]) => {
  const styled = cols.find(col => col.headerStyle);
  if (styled) return cleanName(styled.name);
  return cols[0]?.unit === "BLANK" ? cleanName(cols[0].name) : "";
};

export interface IRowInfo {
  label: string;
  // The heading row above, as in "GENS" over its "RPM" row.
  group: string;
  // Tells the row apart from its namesakes; empty for a row without a name.
  key: string;
  // The heading row that names the columns, as in "Port" and "Stbd".
  columnLabels?: ICol[];
}

// Names the rows of a table whose rows are the measurements, so a row is
// found again in an entry with a row more or less.
export const describeRows = (rows: Array<{ cols: ICol[] }>): IRowInfo[] => {
  let group = "";
  let columnLabels: ICol[] | undefined;
  const seen = new Map<string, number>();

  return rows.map(({ cols }) => {
    const label = getRowLabel(cols);
    if (cols.length > 0 && cols.every(isLabelCell)) {
      // A heading starts a group; a blank spacer ends it.
      group = label;
      if (cols.filter(col => cleanName(col.name)).length > 1)
        columnLabels = cols;
      return { label, group: "", key: "" };
    }
    if (!label) return { label, group, key: "" };

    const name = `${group}|${label}`;
    const count = seen.get(name) ?? 0;
    seen.set(name, count + 1);
    return { label, group, key: `${name}|${count}`, columnLabels };
  });
};

// The fixed text a value sits behind. The row's own name reaches every value
// up to the next text; a nearer one, as in "LAST 24hrs", only the next value.
export const getCellLabel = (cols: ICol[], leafIndex: number) => {
  let label = "";
  let reach = 0;
  let offset = 0;
  for (const [index, cell] of cols.entries()) {
    if (offset >= leafIndex) break;
    offset += getSpan(cell);
    if (isLabelCell(cell)) {
      label = cleanName(cell.name);
      reach = index === 0 ? Infinity : offset;
    }
  }
  return leafIndex <= reach ? label : "";
};

export const getColumnLabel = (row: IRowInfo, leafIndex: number) =>
  row.columnLabels
    ? cleanName(getCellAtLeafIndex(row.columnLabels, leafIndex)?.name)
    : "";

// Mirrors Row.tsx's colSpan accounting.
const getSpan = (cell: any): number =>
  cell.colspan
    ? cell.colspan
    : cell.span
      ? 1 + cell.span
      : !cell.full
        ? 1
        : 999;

// The time a column of readings was taken at, from the table's row of times.
const getSlot = (rows: Array<{ cols: ICol[] }>, leafIndex: number) => {
  for (const { cols } of rows) {
    const cell = getCellAtLeafIndex(cols, leafIndex);
    if (cell?.unit !== "time" && cell?.unit !== "24lock") continue;
    const time =
      cell.value && typeof cell.value === "object"
        ? cell.value.time
        : cell.value;
    return typeof time === "string" && time.trim() ? time.trim() : undefined;
  }
  return undefined;
};

// The column's name, after the names of the header groups it sits under.
export const getColumnPath = (
  sectionHeaders: unknown,
  leafIndex: number,
): string[] => {
  const walk = (headers: ICol[], offset: number): string[] | null => {
    for (const header of headers) {
      const isGroup =
        header.type !== "space" && !!header.cols && header.cols.length > 0;
      const size = isGroup ? getLeafColumns(header.cols ?? []).length : 1;
      if (leafIndex < offset + size) {
        const name = cleanName(header.name);
        const rest = isGroup ? walk(header.cols ?? [], offset) : [];
        // A group can carry the same name as its only column.
        return rest
          ? [name, ...rest].filter(
              (part, index, parts) => part && part !== parts[index - 1],
            )
          : null;
      }
      offset += size;
    }
    return null;
  };
  return walk(getWorkingHeaders(sectionHeaders), 0) ?? [];
};

// An untitled table reads as part of the titled one above it.
export const getSectionTitle = (
  sections: ISection[] | undefined,
  sectionIndex: number,
) => {
  for (let index = sectionIndex; index >= 0; index--) {
    const title = cleanName(sections?.[index]?.title);
    if (title) return title;
  }
  return "";
};

// The name a column is charted by; null where it cannot be charted.
export const getChartColumn = (sectionHeaders: unknown, leafIndex: number) => {
  const headers = getWorkingHeaders(sectionHeaders);
  const header = getLeafColumns(headers)[leafIndex];
  if (!header?.name || header.type === "space" || header.chartable === false)
    return null;
  // Columns are charted by name: a repeated name would read another column.
  return findLeafColumnIndex(headers, header.name) === leafIndex
    ? header.name
    : null;
};

// The cell as a chart of its own, where its row keeps its place.
export const getChartCell = (
  entry: IReportEntry,
  template: ISection | undefined,
  ref: ICellRef,
  // Cells already picked in the section, to keep the names apart.
  picked: IChartCell[] = [],
  entries: IReportEntry[] = [entry],
): IChartCell | null => {
  const section = entry.lbentry?.sections?.[ref.sectionIndex];
  if (
    !section ||
    isDynamicSection(template) ||
    !hasFixedRows(entries, entry.lbid, ref.sectionIndex)
  )
    return null;

  const col = getChartColumn((template ?? section).headers, ref.leafIndex);
  if (!col) return null;

  const rows = getSectionRows(section.rows);
  const rowIndex = rows.findIndex(
    row => row.rowIndex === ref.rowIndex && row.subRowIndex === ref.subRowIndex,
  );
  if (rowIndex === -1) return null;
  // A table of one unnamed row is its columns: the column charts the same.
  if (rows.length === 1 && !getRowLabel(rows[0].cols)) return null;

  const cellKey = `${toTrendCell(ref.sectionIndex, ref.rowIndex, ref.subRowIndex)}:${ref.leafIndex}`;
  const row = getRowLabel(rows[rowIndex].cols) || `Row ${rowIndex + 1}`;
  const name = `${row} – ${cleanName(col)}`;
  const taken = picked.some(
    cell => cell.name === name && cell.cellKey !== cellKey,
  );
  return {
    col,
    rowIndex,
    name: taken ? `${name} (row ${rowIndex + 1})` : name,
    cellKey,
  };
};

interface ISeriesParams {
  entries: IReportEntry[];
  lbid: string;
  sectionIndex: number;
  leafIndex: number;
  // The column's name: an older entry laid out differently is left out.
  name?: string;
  // Omitted for a dynamic section: every row of the column.
  row?: Pick<ICellRef, "rowIndex" | "subRowIndex">;
  newestRowFirst?: boolean;
  // The hovered entry, where it is a draft or deleted.
  own?: string;
}

// Chartable entries, oldest first. A draft or deleted entry is in no one
// else's trend but sees its own: `own` lets it in.
const getDatedEntries = (entries: IReportEntry[], lbid: string, own?: string) =>
  entries
    .filter(
      entry =>
        entry.lbid === lbid &&
        (isChartEntry(entry, true) || entry.lbeid === own),
    )
    .map(entry => ({
      entry,
      time: dayjs(getEntryLocalDate(entry)).valueOf(),
    }))
    // An entry without a date has no place in time.
    .filter(({ time }) => Number.isFinite(time) && time >= EARLIEST_TIME)
    .sort((a, b) => a.time - b.time);

interface IRowSeriesParams {
  entries: IReportEntry[];
  lbid: string;
  sectionIndex: number;
  rowKey: string;
  // One column of the row; omitted to take the row's readings side by side.
  leafIndex?: number;
  // With the readings: only those under this column label, as in "Port".
  columnLabel?: string;
  own?: string;
}

// A named row across entries: either one of its columns, or, where the
// columns are readings taken through the day, all of them in order.
export const buildRowSeries = ({
  entries,
  lbid,
  sectionIndex,
  rowKey,
  leafIndex,
  columnLabel = "",
  own,
}: IRowSeriesParams): ICellPoint[] =>
  getDatedEntries(entries, lbid, own).flatMap(({ entry, time }) => {
    const section = entry.lbentry?.sections?.[sectionIndex];
    if (!section || section.subSections) return [];
    const rows = getSectionRows(section.rows);
    const infos = describeRows(rows);
    const at = infos.findIndex(info => info.key === rowKey);
    if (at === -1) return [];

    const { cols, rowIndex, subRowIndex } = rows[at];
    let offset = 0;
    return cols.flatMap(cell => {
      const start = offset;
      offset += getSpan(cell);
      const wanted =
        leafIndex === undefined
          ? getColumnLabel(infos[at], start) === columnLabel
          : leafIndex >= start && leafIndex < offset;
      if (!wanted || !isTrendValue(cell)) return [];
      return [
        {
          time,
          value: Number(extractCellValue(cell)),
          lbeid: entry.lbeid,
          rowIndex,
          subRowIndex,
          leafIndex: start,
          slot: leafIndex === undefined ? getSlot(rows, start) : undefined,
        },
      ];
    });
  });

export const buildCellSeries = ({
  entries,
  lbid,
  sectionIndex,
  leafIndex,
  name,
  row,
  newestRowFirst,
  own,
}: ISeriesParams): ICellPoint[] =>
  getDatedEntries(entries, lbid, own).flatMap(({ entry, time }) => {
    const section = entry.lbentry?.sections?.[sectionIndex];
    if (!section || section.subSections) return [];
    const rows = getSectionRows(section.rows);
    const picked = row
      ? rows.filter(
          r => r.rowIndex === row.rowIndex && r.subRowIndex === row.subRowIndex,
        )
      : newestRowFirst
        ? [...rows].reverse()
        : rows;
    return picked.flatMap(({ cols, rowIndex, subRowIndex }) => {
      const cell = getCellAtLeafIndex(cols, leafIndex);
      const moved = !!name && !!cell?.name && cell.name !== name;
      return !moved && isTrendValue(cell)
        ? [
            {
              time,
              value: Number(extractCellValue(cell)),
              lbeid: entry.lbeid,
              rowIndex,
              subRowIndex,
            },
          ]
        : [];
    });
  });

const compare = (
  label: string,
  current: ICellPoint,
  point?: ICellPoint,
): ICellComparison => {
  if (!point) return { label };
  const delta = round2(current.value - point.value);
  return {
    label,
    point,
    delta,
    percent:
      point.value === 0
        ? undefined
        : round2((delta / Math.abs(point.value)) * 100),
  };
};

// "Last value" and "Last week" on the same value read "Last value / week".
const mergeComparisons = (comparisons: ICellComparison[]) =>
  comparisons.reduce<ICellComparison[]>((merged, comparison) => {
    const previous = merged[merged.length - 1];
    if (!previous || previous.point !== comparison.point)
      return [...merged, comparison];
    return [
      ...merged.slice(0, -1),
      {
        ...previous,
        label: `${previous.label} / ${comparison.label.replace("Last ", "")}`,
      },
    ];
  }, []);

export const getCellTrend = (
  series: ICellPoint[],
  ref: Pick<ICellRef, "lbeid" | "rowIndex" | "subRowIndex"> &
    Partial<Pick<ICellRef, "leafIndex">>,
): ICellTrend | null => {
  const index = series.findIndex(
    point =>
      point.lbeid === ref.lbeid &&
      point.rowIndex === ref.rowIndex &&
      point.subRowIndex === ref.subRowIndex &&
      (point.leafIndex === undefined || point.leafIndex === ref.leafIndex),
  );
  if (index === -1) return null;
  const current = series[index];
  const earlier = series.slice(0, index);
  const values = series.map(point => point.value);

  return {
    current,
    history: series.slice(0, index + 1),
    stats: {
      min: series.reduce((min, point) =>
        point.value <= min.value ? point : min,
      ),
      max: series.reduce((max, point) =>
        point.value >= max.value ? point : max,
      ),
      average: round2(
        values.reduce((sum, value) => sum + value, 0) / values.length,
      ),
      count: values.length,
    },
    comparisons: mergeComparisons([
      compare("Last value", current, earlier[earlier.length - 1]),
      ...WINDOWS.map(({ label, days }) => {
        // Whole days, so the time of day does not push an entry out.
        const limit = dayjs(current.time)
          .subtract(days, "day")
          .endOf("day")
          .valueOf();
        return compare(
          label,
          current,
          earlier.filter(point => point.time <= limit).pop(),
        );
      }),
    ]),
  };
};
